import { Link } from "react-router-dom";
import PageBanner from "@/components/feature/PageBanner";
import SectionHeading from "@/components/base/SectionHeading";
import Reveal from "@/components/feature/Reveal";

const values = [
  {
    icon: "ri-focus-3-line",
    title: "결을 보는 안목",
    desc: "같은 이름의 석재라도 결의 흐름과 색의 균일도는 판재마다 다릅니다. 동우스톤은 원석을 직접 선별해 공간에 어울리는 판재만을 골라 공급합니다.",
  },
  {
    icon: "ri-hand-heart-line",
    title: "끝까지 책임지는 시공",
    desc: "현장 실측부터 줄눈 처리, 마감 광택까지 담당 팀이 직접 관리합니다. 소재와 시공이 따로 놀지 않도록 한 팀이 책임집니다.",
  },
  {
    icon: "ri-leaf-line",
    title: "오래 쓰는 소재",
    desc: "유행을 좇기보다 시간이 지나도 변하지 않는 톤과 질감을 제안합니다. 빠르게 지나가는 인테리어가 아니라 오래 머무는 공간을 만듭니다.",
  },
];

const process = [
  { step: "01", title: "상담 · 실측", desc: "도면과 현장 사진을 바탕으로 용도, 예산, 일정을 함께 정리합니다." },
  { step: "02", title: "소재 선정", desc: "전시장에서 실물을 비교하고, 공간에 맞는 소재와 마감을 고릅니다." },
  { step: "03", title: "가공 · 시공", desc: "정밀 가공 후 현장 조건에 맞춰 시공하고 줄눈과 광택을 마감합니다." },
  { step: "04", title: "사후 관리", desc: "시공 후 관리 방법을 안내하고, 정기 점검으로 상태를 확인합니다." },
];

export default function Brand() {
  return (
    <>
      <PageBanner
        eyebrow="BRAND"
        title="브랜드"
        description="석재를 단순한 자재가 아니라 공간의 분위기를 결정하는 요소로 봅니다. 동우스톤이 소재를 고르고 다루는 기준을 소개합니다."
        image="https://readdy.ai/api/search-image?query=elegant%20stone%20showroom%20interior%20with%20natural%20light%20falling%20on%20large%20marble%20slabs%20standing%20upright%2C%20minimal%20warm%20neutral%20space%2C%20high%20end%20architectural%20photography%2C%20soft%20shadows%2C%20refined%20atmosphere&width=1800&height=900&seq=stone-banner-brand&orientation=landscape"
        imageAlt="대리석 판재가 전시된 동우스톤 쇼룸"
        current="브랜드"
      />

      {/* Story */}
      <section className="bg-background-50">
        <div className="mx-auto max-w-content px-4 py-16 md:px-6 md:py-24">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-16">
            <Reveal>
              <SectionHeading
                eyebrow="OUR STORY"
                title={
                  <>
                    1994년, 작은 석재상에서
                    <br />
                    시작했습니다
                  </>
                }
                description="처음에는 대리석 한 종류를 다루는 작은 가공 공장이었습니다. 좋은 원석을 찾아 전국을 다니고, 결이 고른 판재만 남기는 일을 반복하다 보니 자연스럽게 소재를 보는 기준이 생겼습니다."
              />
              <div className="mt-6 space-y-4 text-sm leading-relaxed text-foreground-600 md:text-base">
                <p>
                  지금은 천연대리석, 화강석, 트래버틴은 물론 엔지니어드 스톤과 포세린 세라믹까지 1,200여 종의
                  소재를 다루는 종합 석재 브랜드가 되었습니다. 하지만 기준은 그때와 같습니다.
                </p>
                <p>
                  화려한 카탈로그보다 실제 공간에서 어떻게 보이는지를 먼저 생각합니다. 그래서 동우스톤의 모든
                  제품 페이지와 시공 사례에는 실제 공간의 실사 이미지를 함께 담았습니다.
                </p>
              </div>
              <div className="mt-8 grid grid-cols-2 gap-4">
                <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                  <p className="font-heading text-3xl text-primary-600">32년</p>
                  <p className="mt-1 text-xs text-foreground-600">석재를 다뤄온 시간</p>
                </div>
                <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                  <p className="font-heading text-3xl text-accent-700">1,200+</p>
                  <p className="mt-1 text-xs text-foreground-600">보유 소재 컬렉션</p>
                </div>
              </div>
            </Reveal>
            <Reveal delay={120}>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 h-[260px] overflow-hidden rounded-lg md:h-[320px]">
                  <img
                    src="https://readdy.ai/api/search-image?query=interior%20of%20a%20stone%20workshop%20with%20large%20marble%20slabs%20being%20processed%2C%20industrial%20equipment%2C%20warm%20daylight%20through%20high%20windows%2C%20neutral%20dust%20free%20space%2C%20documentary%20architectural%20photography&width=1200&height=800&seq=stone-brand-story-01&orientation=landscape"
                    alt="동우스톤 석재 가공 현장"
                    title="동우스톤 석재 가공 공정"
                    className="h-full w-full object-cover object-top"
                    loading="lazy"
                  />
                </div>
                <div className="h-[200px] overflow-hidden rounded-lg md:h-[240px]">
                  <img
                    src="https://readdy.ai/api/search-image?query=close%20up%20of%20natural%20stone%20slab%20surface%20showing%20fine%20veins%20and%20mineral%20texture%2C%20soft%20studio%20lighting%2C%20neutral%20background%2C%20artisanal%20material%20photography%2C%20high%20detail&width=800&height=1000&seq=stone-brand-story-02&orientation=portrait"
                    alt="석재 표면의 미세한 결 확대"
                    title="동우스톤 석재 결"
                    className="h-full w-full object-cover object-top"
                    loading="lazy"
                  />
                </div>
                <div className="h-[200px] overflow-hidden rounded-lg md:h-[240px]">
                  <img
                    src="https://readdy.ai/api/search-image?query=craftsman%20measuring%20a%20stone%20slab%20with%20a%20steel%20ruler%20in%20a%20bright%20workspace%2C%20hands%20in%20focus%2C%20warm%20neutral%20lighting%2C%20minimal%20background%2C%20documentary%20photography&width=800&height=1000&seq=stone-brand-story-03&orientation=portrait"
                    alt="석재 실측 작업"
                    title="동우스톤 정밀 실측"
                    className="h-full w-full object-cover object-top"
                    loading="lazy"
                  />
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="bg-background-100">
        <div className="mx-auto max-w-content px-4 py-16 md:px-6 md:py-24">
          <Reveal>
            <SectionHeading
              align="center"
              eyebrow="OUR VALUE"
              title="동우스톤이 소재를 다루는 기준"
              description="좋은 공간은 좋은 소재에서 시작하지만, 결국 완성하는 것은 사람의 손입니다."
            />
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-3">
            {values.map((value, index) => (
              <Reveal key={value.title} delay={index * 90}>
                <div className="h-full rounded-lg border border-background-200 bg-background-50 p-7">
                  <span className="flex h-12 w-12 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                    <i className={`${value.icon} text-2xl`} />
                  </span>
                  <h3 className="mt-5 font-heading text-xl text-foreground-950">{value.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-foreground-600">{value.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="bg-background-50">
        <div className="mx-auto max-w-content px-4 py-16 md:px-6 md:py-24">
          <Reveal>
            <SectionHeading
              eyebrow="PROCESS"
              title="소재가 공간이 되기까지"
              description="상담 한 번으로 끝나지 않습니다. 네 단계에 걸쳐 공간을 완성합니다."
            />
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {process.map((item, index) => (
              <Reveal key={item.step} delay={index * 80}>
                <div className="h-full rounded-lg border border-background-200 bg-background-100 p-6">
                  <span className="font-heading text-3xl text-primary-500">{item.step}</span>
                  <h3 className="mt-4 font-heading text-lg text-foreground-950">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-accent-900">
        <div className="mx-auto flex max-w-content flex-col items-center gap-6 px-4 py-14 text-center md:px-6 md:py-16">
          <h2 className="max-w-2xl font-heading text-2xl leading-snug text-background-50 md:text-3xl">
            전시장에서 실물로 확인해 보세요
          </h2>
          <p className="max-w-xl text-sm leading-relaxed text-background-200/80">
            서울 본사와 부산 전시장에서 1,200여 종의 소재를 직접 비교하실 수 있습니다. 방문 전 상담을 예약하시면
            담당자가 준비해 안내해 드립니다.
          </p>
          <Link
            to="/contact"
            className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-background-50 px-7 py-3.5 text-sm font-medium text-accent-900 transition-colors hover:bg-background-100"
          >
            방문 상담 예약하기 <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </section>
    </>
  );
}